<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { formatApiError } from '@yuksalish/api-client'
import { useAuthStore } from '@yuksalish/auth'
import { useHrApi } from '../composables/useHrApi'
import EmployeePickerModal from '../components/EmployeePickerModal.vue'
import DepartmentPickerModal from '../components/DepartmentPickerModal.vue'
import EmployeeCardsSection from '../components/EmployeeCardsSection.vue'
import { MAX_IMAGE_BYTES, isAllowedImage, readFileAsDataUrl, resolveMediaUrl } from '../utils/files'
import type { BillingMode, CurrencyOption, Employee, EmployeePayload, EmployeeType, Option } from '../types/hr'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const auth = useAuthStore()
const { getEmployee, createEmployee, updateEmployee, listCurrencies, nextEmployeeCode, createEmployeeCard } = useHrApi()

const employeeId = computed(() => (route.params.id ? Number(route.params.id) : null))
const isEdit = computed(() => employeeId.value !== null)
const canViewCards = computed(() => (auth.user?.permissions ?? []).includes('kiosk.view'))

/** Loaded record — supplies the name shown in the page header. */
const loadedEmployee = ref<Employee | null>(null)
/** Set when a create succeeded but the queued cards still need registration. */
const createdEmployeeId = ref<number | null>(null)
/** Card UIDs entered before the employee exists; posted once the record is created. */
const pendingCardUids = ref<string[]>([])

const loadedName = computed(() => {
  const employee = loadedEmployee.value
  if (!employee) return ''
  const value = `${employee.last_name} ${employee.first_name}`.trim()
  return value || employee.employee_code
})

const headingTitle = computed(() =>
  isEdit.value ? loadedName.value || t('hr.employeeForm.editTitle') : t('hr.employeeForm.createTitle'),
)

const selectedDepartment = ref<Option | null>(null)
const showDepartmentPicker = ref(false)
const currencies = ref<CurrencyOption[]>([])
const currenciesAvailable = ref(true)
const supervisor = ref<Employee | null>(null)
const showSupervisorPicker = ref(false)

const loading = ref(true)
const saving = ref(false)
const error = ref('')
const actionError = ref('')

const photoInput = ref<HTMLInputElement | null>(null)
const photoPreview = ref('')
/** New photo as a data URI, ready for `photo_base64`. */
const photoData = ref<string | null>(null)
/** `true` when the stored photo should be removed on save. */
const photoCleared = ref(false)

const form = reactive({
  employee_code: '',
  first_name: '',
  last_name: '',
  middle_name: '',
  phone: '',
  email: '',
  birthday: '',
  id_passport_number: '',
  tax_id: '',
  bank_card_number: '',
  emergency_contact_name: '',
  emergency_contact_number: '',
  home_address: '',
  department_agent_id: '' as string | number,
  supervisor_employee_id: null as number | null,
  job_title: '',
  employee_type: 'production' as EmployeeType,
  billing_mode: 'hourly' as BillingMode,
  default_hourly_rate: 0 as number | string,
  default_cycle_rate: 0 as number | string,
  default_fixed_salary: 0 as number | string,
  currency_code: '',
  hire_date: '',
  is_active: true,
})

function applyEmployee(employee: Employee) {
  form.employee_code = employee.employee_code
  form.first_name = employee.first_name
  form.last_name = employee.last_name
  form.middle_name = employee.middle_name ?? ''
  form.phone = employee.phone ?? ''
  form.email = employee.email ?? ''
  form.birthday = employee.birthday ? employee.birthday.slice(0, 10) : ''
  form.id_passport_number = employee.id_passport_number ?? ''
  form.tax_id = employee.tax_id ?? ''
  form.bank_card_number = employee.bank_card_number ?? ''
  form.emergency_contact_name = employee.emergency_contact_name ?? ''
  form.emergency_contact_number = employee.emergency_contact_number ?? ''
  form.home_address = employee.home_address ?? ''
  form.department_agent_id = employee.department_agent_id ?? ''
  selectedDepartment.value =
    employee.department_agent_id === null
      ? null
      : {
          id: employee.department_agent_id,
          name: employee.department_name || `#${employee.department_agent_id}`,
        }
  form.supervisor_employee_id = employee.supervisor_employee_id ?? null
  form.job_title = employee.job_title ?? ''
  form.employee_type = employee.employee_type
  form.billing_mode = employee.billing_mode
  form.default_hourly_rate = employee.default_hourly_rate
  form.default_cycle_rate = employee.default_cycle_rate
  form.default_fixed_salary = employee.default_fixed_salary
  form.currency_code = employee.currency_code ?? ''
  form.hire_date = employee.hire_date ? employee.hire_date.slice(0, 10) : ''
  form.is_active = employee.is_active === 1
  photoPreview.value = resolveMediaUrl(employee.photo_url)
  photoData.value = null
  photoCleared.value = false
}

async function loadReferences() {
  try {
    currencies.value = await listCurrencies()
  } catch {
    currencies.value = []
    currenciesAvailable.value = false
  }
}

async function load() {
  loading.value = true
  error.value = ''
  try {
    if (employeeId.value !== null) {
      const employee = await getEmployee(employeeId.value)
      loadedEmployee.value = employee
      applyEmployee(employee)
      if (employee.supervisor_employee_id) {
        supervisor.value = await getEmployee(employee.supervisor_employee_id).catch(() => null)
      }
    } else {
      // Employee codes are generated by the app, not typed by the operator.
      form.employee_code = await nextEmployeeCode().catch(() => '')
    }
  } catch (cause) {
    error.value = formatApiError(cause)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void loadReferences()
  void load()
})

function selectSupervisor(employee: Employee) {
  supervisor.value = employee
  form.supervisor_employee_id = employee.id
  showSupervisorPicker.value = false
}

function selectDepartment(department: Option) {
  selectedDepartment.value = department
  form.department_agent_id = department.id
  showDepartmentPicker.value = false
}

function clearDepartment() {
  selectedDepartment.value = null
  form.department_agent_id = ''
}

async function onPhotoChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0] ?? null
  actionError.value = ''
  if (!file) return

  if (!isAllowedImage(file)) {
    actionError.value = t('hr.employeeForm.photoInvalidType')
    input.value = ''
    return
  }
  if (file.size > MAX_IMAGE_BYTES) {
    actionError.value = t('hr.employeeForm.photoTooLarge')
    input.value = ''
    return
  }

  try {
    const dataUrl = await readFileAsDataUrl(file)
    photoData.value = dataUrl
    photoPreview.value = dataUrl
    photoCleared.value = false
  } catch {
    actionError.value = t('hr.employeeForm.photoInvalidType')
  }
}

function removePhoto() {
  photoData.value = null
  photoPreview.value = ''
  photoCleared.value = true
  if (photoInput.value) photoInput.value.value = ''
}

function clearSupervisor() {
  supervisor.value = null
  form.supervisor_employee_id = null
}

function toNumber(value: number | string): number {
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : 0
}

async function save() {
  actionError.value = ''

  if (!form.first_name.trim() || !form.last_name.trim()) {
    actionError.value = t('hr.employeeForm.invalidRequired')
    return
  }
  if (isEdit.value && !form.employee_code.trim()) {
    actionError.value = t('hr.employeeForm.invalidRequired')
    return
  }
  if (!isEdit.value && !form.employee_code.trim()) {
    form.employee_code = await nextEmployeeCode().catch(() => '')
  }
  if (!form.employee_code.trim()) {
    actionError.value = t('hr.employeeForm.invalidRequired')
    return
  }
  if (toNumber(form.default_hourly_rate) < 0 || toNumber(form.default_cycle_rate) < 0 || toNumber(form.default_fixed_salary) < 0) {
    actionError.value = t('hr.employeeForm.invalidRate')
    return
  }
  if (employeeId.value !== null && form.supervisor_employee_id === employeeId.value) {
    actionError.value = t('hr.employeeForm.supervisorSelf')
    return
  }

  const payload: EmployeePayload = {
    employee_code: form.employee_code.trim(),
    first_name: form.first_name.trim(),
    last_name: form.last_name.trim(),
    middle_name: form.middle_name.trim() || null,
    phone: form.phone.trim() || null,
    email: form.email.trim() || null,
    birthday: form.birthday || null,
    id_passport_number: form.id_passport_number.trim() || null,
    tax_id: form.tax_id.trim() || null,
    bank_card_number: form.bank_card_number.trim() || null,
    emergency_contact_name: form.emergency_contact_name.trim() || null,
    emergency_contact_number: form.emergency_contact_number.trim() || null,
    home_address: form.home_address.trim() || null,
    department_agent_id: form.department_agent_id ? Number(form.department_agent_id) : null,
    supervisor_employee_id: form.supervisor_employee_id,
    job_title: form.job_title.trim() || null,
    employee_type: form.employee_type,
    billing_mode: form.billing_mode,
    default_hourly_rate: toNumber(form.default_hourly_rate),
    default_cycle_rate: toNumber(form.default_cycle_rate),
    default_fixed_salary: toNumber(form.default_fixed_salary),
    currency_code: form.currency_code.trim() ? form.currency_code.trim().toUpperCase() : null,
    hire_date: form.hire_date || null,
    is_active: form.is_active ? 1 : 0,
    ...(photoData.value
      ? { photo_base64: photoData.value }
      : photoCleared.value
        ? { photo_base64: null, photo_url: null }
        : {}),
  }

  saving.value = true
  try {
    const targetId = employeeId.value ?? createdEmployeeId.value
    const saved = targetId === null
      ? await submitCreate(payload)
      : await updateEmployee(targetId, payload)
    createdEmployeeId.value = saved.id
    loadedEmployee.value = saved
    const cardsRegistered = await registerPendingCards(saved.id)
    if (!cardsRegistered) return
    await router.push(`/employees/${saved.id}`)
  } catch (cause) {
    actionError.value = formatApiError(cause)
  } finally {
    saving.value = false
  }
}

/**
 * Issues the cards queued in the create form. Returns `false` when the cards
 * could not be registered, so the form keeps the queue visible for a retry
 * (the employee record itself already exists at that point).
 */
async function registerPendingCards(employeeId: number): Promise<boolean> {
  if (pendingCardUids.value.length === 0) return true
  try {
    for (const uid of pendingCardUids.value) {
      await createEmployeeCard({
        employee_id: employeeId,
        card_uid: uid,
        is_active: 1,
        issued_at: new Date().toISOString().slice(0, 19).replace('T', ' '),
        revoked_at: null,
      })
    }
    pendingCardUids.value = []
    return true
  } catch (cause) {
    actionError.value = `${t('hr.employeeForm.cardsFailed')} ${formatApiError(cause)}`
    return false
  }
}

/**
 * Creates the employee, refreshing the generated code once when another record
 * took it in the meantime (the backend rejects duplicate employee codes).
 */
async function submitCreate(payload: EmployeePayload): Promise<Employee> {
  try {
    return await createEmployee(payload)
  } catch (cause) {
    const retryCode = await nextEmployeeCode().catch(() => '')
    if (!retryCode || retryCode === payload.employee_code) throw cause
    form.employee_code = retryCode
    return await createEmployee({ ...payload, employee_code: retryCode })
  }
}

function supervisorName(): string {
  if (!supervisor.value) return ''
  const name = `${supervisor.value.last_name} ${supervisor.value.first_name}`.trim()
  return name || supervisor.value.employee_code
}
</script>

<template>
  <div class="page-heading">
    <RouterLink class="back" to="/employees">← {{ t('hr.employeeDetail.back') }}</RouterLink>
    <h2>{{ headingTitle }}</h2>
    <p v-if="isEdit">{{ t('hr.employeeForm.editTitle') }} · {{ form.employee_code }}</p>
  </div>

  <div v-if="loading" class="state-message"><p>{{ t('hr.common.loading') }}</p></div>

  <div v-else-if="error" class="state-message error">
    <p>{{ error }}</p>
    <button class="btn-primary" @click="load">{{ t('hr.common.retry') }}</button>
  </div>

  <form v-else class="form-card" @submit.prevent="save">

    <div class="photo-field">
      <img
        v-if="photoPreview"
        class="photo-preview"
        :src="photoPreview"
        :alt="t('hr.employeeForm.photo')"
      >
      <div v-else class="photo-preview photo-preview--empty">{{ t('hr.common.none') }}</div>
      <div class="photo-actions">
        <label class="btn-secondary" for="employee-photo">{{ t('hr.employeeForm.photoChoose') }}</label>
        <input
          id="employee-photo"
          ref="photoInput"
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif"
          @change="onPhotoChange"
        >
        <button v-if="photoPreview" class="link-action danger" type="button" @click="removePhoto">
          {{ t('hr.employeeForm.photoRemove') }}
        </button>
        <small>{{ t('hr.employeeForm.photoHint') }}</small>
      </div>
    </div>
    <div class="form-grid">
      <div class="field">
        <label for="employee-code">{{ t('hr.employeeForm.employeeCode') }}</label>
        <input
          id="employee-code"
          v-model="form.employee_code"
          type="text"
          autocomplete="off"
          required
        >
        <small v-if="!isEdit">{{ t('hr.employeeForm.codeAuto', { code: form.employee_code || '—' }) }}</small>
      </div>
      <div class="field">
        <label for="employee-type">{{ t('hr.employeeForm.employeeType') }}</label>
        <select id="employee-type" v-model="form.employee_type">
          <option value="office">{{ t('hr.employeeType.office') }}</option>
          <option value="production">{{ t('hr.employeeType.production') }}</option>
          <option value="mixed">{{ t('hr.employeeType.mixed') }}</option>
        </select>
      </div>
      <div class="field">
        <label for="employee-first-name">{{ t('hr.employeeForm.firstName') }}</label>
        <input id="employee-first-name" v-model="form.first_name" type="text" required>
      </div>
      <div class="field">
        <label for="employee-last-name">{{ t('hr.employeeForm.lastName') }}</label>
        <input id="employee-last-name" v-model="form.last_name" type="text" required>
      </div>
      <div class="field">
        <label for="employee-middle-name">{{ t('hr.employeeForm.middleName') }}</label>
        <input id="employee-middle-name" v-model="form.middle_name" type="text">
      </div>
      <div class="field">
        <label>{{ t('hr.employeeForm.department') }}</label>
        <button class="field-button" type="button" @click="showDepartmentPicker = true">
          <span :class="{ placeholder: !selectedDepartment }">
            {{ selectedDepartment ? selectedDepartment.name : t('hr.common.none') }}
          </span>
          <span aria-hidden="true">▾</span>
        </button>
        <small>{{ t('hr.employeeForm.departmentHint') }}</small>
        <small v-if="selectedDepartment">
          <button class="link-action danger" type="button" @click="clearDepartment">
            {{ t('hr.common.reset') }}
          </button>
        </small>
      </div>
      <div class="field">
        <label>{{ t('hr.employeeForm.supervisor') }}</label>
        <button class="field-button" type="button" @click="showSupervisorPicker = true">
          <span :class="{ placeholder: !supervisor }">
            {{ supervisor ? supervisorName() : t('hr.common.none') }}
          </span>
          <span aria-hidden="true">▾</span>
        </button>
        <small v-if="supervisor">
          <button class="link-action danger" type="button" @click="clearSupervisor">
            {{ t('hr.common.reset') }}
          </button>
        </small>
      </div>
      <div class="field">
        <label for="employee-job-title">{{ t('hr.employeeForm.jobTitle') }}</label>
        <input id="employee-job-title" v-model="form.job_title" type="text">
      </div>
      <div class="field">
        <label for="employee-hire-date">{{ t('hr.employeeForm.hireDate') }}</label>
        <input id="employee-hire-date" v-model="form.hire_date" type="date">
      </div>
      <div class="check-field">
        <input id="employee-active" v-model="form.is_active" type="checkbox">
        <label for="employee-active">{{ t('hr.employeeForm.active') }}</label>
      </div>
    </div>

    <h3>{{ t('hr.employeeForm.sectionContact') }}</h3>
    <div class="form-grid">
      <div class="field">
        <label for="employee-phone">{{ t('hr.employeeForm.phone') }}</label>
        <input id="employee-phone" v-model="form.phone" type="tel">
      </div>
      <div class="field">
        <label for="employee-email">{{ t('hr.employeeForm.email') }}</label>
        <input id="employee-email" v-model="form.email" type="email">
      </div>
      <div class="field field-wide">
        <label for="employee-address">{{ t('hr.employeeForm.homeAddress') }}</label>
        <input id="employee-address" v-model="form.home_address" type="text">
      </div>
      <div class="field">
        <label for="employee-emergency-name">{{ t('hr.employeeForm.emergencyName') }}</label>
        <input id="employee-emergency-name" v-model="form.emergency_contact_name" type="text">
      </div>
      <div class="field">
        <label for="employee-emergency-number">{{ t('hr.employeeForm.emergencyNumber') }}</label>
        <input id="employee-emergency-number" v-model="form.emergency_contact_number" type="tel">
      </div>
    </div>

    <h3>{{ t('hr.employeeForm.sectionPersonal') }}</h3>
    <div class="form-grid">
      <div class="field">
        <label for="employee-birthday">{{ t('hr.employeeForm.birthday') }}</label>
        <input id="employee-birthday" v-model="form.birthday" type="date">
      </div>
      <div class="field">
        <label for="employee-passport">{{ t('hr.employeeForm.passport') }}</label>
        <input id="employee-passport" v-model="form.id_passport_number" type="text">
      </div>
      <div class="field">
        <label for="employee-tax-id">{{ t('hr.employeeForm.taxId') }}</label>
        <input id="employee-tax-id" v-model="form.tax_id" type="text">
      </div>
      <div class="field">
        <label for="employee-bank-card">{{ t('hr.employeeForm.bankCard') }}</label>
        <input id="employee-bank-card" v-model="form.bank_card_number" type="text">
      </div>
    </div>

    <h3>{{ t('hr.employeeForm.sectionCompensation') }}</h3>
    <div class="form-grid">
      <div class="field">
        <label for="employee-billing-mode">{{ t('hr.employeeForm.billingMode') }}</label>
        <select id="employee-billing-mode" v-model="form.billing_mode">
          <option value="hourly">{{ t('hr.billingMode.hourly') }}</option>
          <option value="production_cycle">{{ t('hr.billingMode.production_cycle') }}</option>
          <option value="fixed_salary">{{ t('hr.billingMode.fixed_salary') }}</option>
          <option value="fixed_task">{{ t('hr.billingMode.fixed_task') }}</option>
        </select>
      </div>
      <div class="field">
        <label for="employee-hourly-rate">{{ t('hr.employeeForm.hourlyRate') }}</label>
        <input id="employee-hourly-rate" v-model="form.default_hourly_rate" type="number" min="0" step="0.01">
      </div>
      <div class="field">
        <label for="employee-cycle-rate">{{ t('hr.employeeForm.cycleRate') }}</label>
        <input id="employee-cycle-rate" v-model="form.default_cycle_rate" type="number" min="0" step="0.01">
      </div>
      <div class="field">
        <label for="employee-fixed-salary">{{ t('hr.employeeForm.fixedSalary') }}</label>
        <input id="employee-fixed-salary" v-model="form.default_fixed_salary" type="number" min="0" step="0.01">
      </div>
      <div class="field">
        <label for="employee-currency">{{ t('hr.employeeForm.currency') }}</label>
        <select v-if="currenciesAvailable && currencies.length" id="employee-currency" v-model="form.currency_code">
          <option value="">{{ t('hr.common.none') }}</option>
          <option v-for="currency in currencies" :key="currency.id" :value="currency.code">
            {{ currency.code }}{{ currency.name ? ' — ' + currency.name : '' }}
          </option>
        </select>
        <input
          v-else
          id="employee-currency"
          v-model="form.currency_code"
          type="text"
          maxlength="3"
          :placeholder="t('hr.employeeForm.currencyFallback')"
        >
        <small>{{ currenciesAvailable ? t('hr.employeeForm.currencyHint') : t('hr.employeeForm.currenciesUnavailable') }}</small>
      </div>
    </div>

    <EmployeeCardsSection
      v-if="!isEdit && canViewCards"
      v-model:pending="pendingCardUids"
      embedded
    />

    <p v-if="actionError" class="error-inline">{{ actionError }}</p>

    <div class="form-actions">
      <button class="btn-primary" type="submit" :disabled="saving">
        {{ saving ? t('hr.common.saving') : isEdit ? t('hr.employeeForm.submitUpdate') : t('hr.employeeForm.submitCreate') }}
      </button>
      <RouterLink class="btn-secondary" to="/employees">{{ t('hr.common.cancel') }}</RouterLink>
    </div>
  </form>

  <EmployeeCardsSection
    v-if="isEdit && employeeId !== null && canViewCards"
    :employee-id="employeeId"
  />

  <EmployeePickerModal
    :open="showSupervisorPicker"
    :title="t('hr.picker.supervisorTitle')"
    :eyebrow="t('hr.picker.supervisorEyebrow')"
    :search-label="t('hr.picker.supervisorSearch')"
    :search-placeholder="t('hr.picker.supervisorPlaceholder')"
    :empty-message="t('hr.picker.supervisorEmpty')"
    :exclude-employee-id="employeeId"
    @close="showSupervisorPicker = false"
    @select="selectSupervisor"
  />

  <DepartmentPickerModal
    :open="showDepartmentPicker"
    :selected-id="selectedDepartment?.id ?? null"
    @close="showDepartmentPicker = false"
    @select="selectDepartment"
  />
</template>
